"use client";

import { useEffect } from "react";

import { isShellUserAgent } from "@/lib/shell";
import {
  isImmediateRepeat,
  oauthReturnToCallbackPath,
  universalLinkToPath,
} from "@/lib/shell-deep-link";

/** One deep link = one navigation, across documents. The native side both
 *  retains the launch URL for the process lifetime (getLaunchUrl) and
 *  retains the appUrlOpen event until a listener consumes it — without this
 *  guard, the OAuth return would be re-delivered on every full-document
 *  mount and replay the already-consumed code into /auth/callback (whose
 *  failed exchange then trips the #979 retry into a Safari bounce loop).
 *  That's also why the bridge deliberately does NOT call getLaunchUrl: the
 *  retained appUrlOpen event already covers cold starts, exactly once. */
const CONSUMED_KEY = "mc_shell_consumed_deep_link";
const RECENT_LINK_KEY = "mc_shell_recent_universal_link";

/** Mounted once in the root layout. In the native shell, opens universal
 *  links (`universalLinkToPath`) in the webview, and forwards OAuth
 *  deep-link returns (`medceptor://oauth-return?code=...`) into the
 *  webview's /auth/callback, where the PKCE verifier cookie lives — the
 *  return half of the shell's OAuth flow (the outbound half is
 *  OAuthForm + buildShellReturnUrl). */
export function ShellBridge() {
  useEffect(() => {
    if (!isShellUserAgent(navigator.userAgent)) return;
    const app = window.Capacitor?.Plugins?.App;
    if (!app) return;

    const listener = app.addListener("appUrlOpen", (data) => {
      // A universal link (an app.medceptor.com email/invite link iOS opened
      // the app with): load it in the webview, where the session belongs.
      // Same once-only guard as the OAuth return: the event is retained
      // across document mounts, and a confirm link must not reload forever.
      const linkPath = universalLinkToPath(data.url, window.location.host);
      if (linkPath) {
        // Ignore only an immediate repeat of the same link (isImmediateRepeat);
        // tapping it again later must still open it.
        let repeat = false;
        try {
          const now = Date.now();
          const prev = JSON.parse(
            sessionStorage.getItem(RECENT_LINK_KEY) ?? "null",
          ) as { url: string; at: number } | null;
          repeat = isImmediateRepeat(prev, data.url, now);
          sessionStorage.setItem(
            RECENT_LINK_KEY,
            JSON.stringify({ url: data.url, at: now }),
          );
        } catch {
          // Storage unavailable: proceed (one extra load at worst).
        }
        if (!repeat) window.location.assign(linkPath);
        return;
      }
      const path = oauthReturnToCallbackPath(data.url);
      if (!path) return;
      // Arm the guard only for code-carrying returns: codes are unique, so
      // the key can't collide across attempts. ERROR returns are
      // deterministic (two user-cancels produce byte-identical URLs) and
      // idempotent to replay — deduping them would swallow the second
      // cancel's navigation entirely.
      if (data.url.includes("code=")) {
        try {
          if (sessionStorage.getItem(CONSUMED_KEY) === data.url) return;
          sessionStorage.setItem(CONSUMED_KEY, data.url);
        } catch {
          // Storage unavailable — proceed; worst case is a replayed
          // navigation whose stale code fails loudly at the callback.
        }
      }
      window.location.assign(path);
    });
    return () => listener.remove();
  }, []);

  return null;
}
